const STORAGE_VERIFIER = 'corporate-cms-pkce-verifier';
const STORAGE_STATE = 'corporate-cms-pkce-state';
const STORAGE_RETURN_TO = 'corporate-cms-pkce-return-to';

function base64UrlEncode(bytes: ArrayBuffer): string {
  const bin = String.fromCharCode(...new Uint8Array(bytes));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes.buffer).slice(0, length);
}

async function sha256(input: string): Promise<ArrayBuffer> {
  const data = new TextEncoder().encode(input);
  return crypto.subtle.digest('SHA-256', data);
}

export async function createPkceChallenge(): Promise<{ verifier: string; challenge: string }> {
  const verifier = randomString(64);
  const challenge = base64UrlEncode(await sha256(verifier));
  return { verifier, challenge };
}

export function saveVerifier(verifier: string, state: string, returnTo: string): void {
  sessionStorage.setItem(STORAGE_VERIFIER, verifier);
  sessionStorage.setItem(STORAGE_STATE, state);
  sessionStorage.setItem(STORAGE_RETURN_TO, returnTo);
}

export function consumeVerifier(state: string): { verifier: string; returnTo: string } | null {
  const stored = sessionStorage.getItem(STORAGE_STATE);
  if (stored !== state) return null;
  const verifier = sessionStorage.getItem(STORAGE_VERIFIER);
  const returnTo = sessionStorage.getItem(STORAGE_RETURN_TO) || '/';
  sessionStorage.removeItem(STORAGE_VERIFIER);
  sessionStorage.removeItem(STORAGE_STATE);
  sessionStorage.removeItem(STORAGE_RETURN_TO);
  return verifier ? { verifier, returnTo } : null;
}

export function buildAuthorizeUrl(params: {
  gitlabBaseUrl: string;
  clientId: string;
  redirectUri: string;
  challenge: string;
  state: string;
  scope: string;
}): string {
  const qs = new URLSearchParams({
    client_id: params.clientId,
    redirect_uri: params.redirectUri,
    response_type: 'code',
    scope: params.scope,
    state: params.state,
    code_challenge: params.challenge,
    code_challenge_method: 'S256',
  });
  return `${params.gitlabBaseUrl}/oauth/authorize?${qs.toString()}`;
}

export async function exchangeCodeForToken(params: {
  gitlabBaseUrl: string;
  clientId: string;
  redirectUri: string;
  code: string;
  verifier: string;
}): Promise<{ access_token: string; token_type: string; expires_in?: number; refresh_token?: string; created_at?: number }> {
  const body = new URLSearchParams({
    client_id: params.clientId,
    redirect_uri: params.redirectUri,
    grant_type: 'authorization_code',
    code: params.code,
    code_verifier: params.verifier,
  });
  const response = await fetch(`${params.gitlabBaseUrl}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`OAuth token exchange failed: ${response.status} ${text}`);
  }
  return response.json();
}
